import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Layers, Activity, Zap, ShieldCheck, Globe,
  ArrowRight, CheckCircle, ArrowLeft, ChevronDown
} from 'lucide-react';
import { ContactSection } from '../components/ContactSection';

/* ─── Shared primitives ─────────────────────────── */
type Particle = { x:number; y:number; vx:number; vy:number; r:number; pulse:number };

const ParticleCanvas = ({ color='#ff5d22', count=60 }: { color?:string; count?:number }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let w:number, h:number, pts:Particle[], raf:number;
    const init = () => {
      w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight;
      pts = Array.from({ length:count }, () => ({
        x:Math.random()*w, y:Math.random()*h,
        vx:(Math.random()-.5)*.5, vy:(Math.random()-.5)*.5,
        r:Math.random()*1.8+.5, pulse:Math.random()*Math.PI*2,
      }));
    };
    const [r,g,b] = [1,3,5].map((_,i)=>parseInt(color.slice(1+i*2,3+i*2),16));
    const draw = () => {
      ctx.clearRect(0,0,w,h);
      pts.forEach(p=>{ p.x+=p.vx; p.y+=p.vy; p.pulse+=.02;
        if(p.x<0)p.x=w; if(p.x>w)p.x=0; if(p.y<0)p.y=h; if(p.y>h)p.y=0; });
      for(let i=0;i<pts.length;i++) for(let j=i+1;j<pts.length;j++){
        const d=Math.hypot(pts[i].x-pts[j].x,pts[i].y-pts[j].y);
        if(d<120){ ctx.beginPath(); ctx.strokeStyle=`rgba(${r},${g},${b},${(1-d/120)*.22})`;
          ctx.lineWidth=.7; ctx.moveTo(pts[i].x,pts[i].y); ctx.lineTo(pts[j].x,pts[j].y); ctx.stroke(); }
      }
      pts.forEach(p=>{ const pr=p.r*(1+.3*Math.sin(p.pulse));
        const g2=ctx.createRadialGradient(p.x,p.y,0,p.x,p.y,pr*3);
        g2.addColorStop(0,`rgba(${r},${g},${b},.9)`); g2.addColorStop(1,`rgba(${r},${g},${b},0)`);
        ctx.beginPath(); ctx.arc(p.x,p.y,pr*3,0,Math.PI*2); ctx.fillStyle=g2; ctx.fill(); });
      raf=requestAnimationFrame(draw);
    };
    init(); draw();
    const ro=new ResizeObserver(init); ro.observe(canvas);
    return ()=>{ cancelAnimationFrame(raf); ro.disconnect(); };
  },[color,count]);
  return <canvas ref={ref} style={{position:'absolute',inset:0,width:'100%',height:'100%',pointerEvents:'none'}} />;
};

const Orb = ({ style, color, size, dur, delay=0 }:{style?:React.CSSProperties;color:string;size:number;dur:number;delay?:number}) => (
  <div style={{ position:'absolute', borderRadius:'50%', filter:`blur(${size*.35}px)`,
    width:size, height:size, background:color, opacity:.13,
    animation:`capOrbFloat ${dur}s ease-in-out ${delay}s infinite`, ...style }} />
);

const Reveal = ({ children, delay=0, className='' }:{children:React.ReactNode;delay?:number;className?:string}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [vis,setVis] = useState(false);
  useEffect(()=>{
    const obs = new IntersectionObserver(([e])=>{ if(e.isIntersecting){ setVis(true); obs.disconnect(); }},{threshold:.08});
    if(ref.current) obs.observe(ref.current);
    return ()=>obs.disconnect();
  },[]);
  return (
    <div ref={ref} className={className} style={{
      opacity:vis?1:0, transform:vis?'translateY(0) scale(1)':'translateY(28px) scale(.97)',
      transition:`opacity .65s ease ${delay}s, transform .65s ease ${delay}s`,
    }}>{children}</div>
  );
};

const TCard = ({ children, className='' }:{children:React.ReactNode;className?:string}) => {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div ref={ref} className={className}
      style={{ transition:'transform .28s ease', transformStyle:'preserve-3d' }}
      onMouseMove={e=>{ if(!ref.current)return; const r=ref.current.getBoundingClientRect();
        const x=((e.clientX-r.left)/r.width-.5)*12; const y=((e.clientY-r.top)/r.height-.5)*-12;
        ref.current.style.transform=`perspective(800px) rotateY(${x}deg) rotateX(${y}deg) translateY(-5px)`; }}
      onMouseLeave={()=>{ if(ref.current) ref.current.style.transform='perspective(800px) rotateY(0) rotateX(0) translateY(0)'; }}>
      {children}
    </div>
  );
};

const FAQ = ({ q, a, i }:{q:string;a:string;i:number}) => {
  const [open,setOpen]=useState(false);
  return (
    <Reveal delay={i*.07}>
      <div onClick={()=>setOpen(!open)} className="cursor-pointer mb-3 rounded-2xl border transition-all duration-300"
        style={{ background:open?'linear-gradient(135deg,#0f0f0f,#1a1a1a)':'#fff',
          borderColor:open?'rgba(227,30,36,.45)':'#e5e7eb',
          boxShadow:open?'0 8px 36px rgba(227,30,36,.12)':'0 1px 8px rgba(0,0,0,.04)', padding:'15px 18px' }}>
        <div className="flex items-center justify-between gap-4">
          <span style={{ fontWeight:700, fontSize:13.5, color:open?'#fff':'#111' }}>{q}</span>
          <div style={{ width:26, height:26, borderRadius:'50%', background:open?'#ff5d22':'#f3f4f6',
            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
            transition:'all .28s ease', transform:open?'rotate(45deg)':'none' }}>
            <svg width={10} height={10} viewBox="0 0 14 14" fill="none" stroke={open?'#fff':'#555'} strokeWidth={2.5}><path d="M7 1v12M1 7h12"/></svg>
          </div>
        </div>
        <div style={{ overflow:'hidden', maxHeight:open?200:0, transition:'max-height .38s ease', marginTop:open?10:0 }}>
          <p style={{ fontSize:13, lineHeight:1.65, color:'rgba(255,255,255,.7)', margin:0 }}>{a}</p>
        </div>
      </div>
    </Reveal>
  );
};

/* ─── Capability data ───────────────────────────── */
const CAPS = [
  {
    slug: 'adaptive-onboarding',
    title: 'Adaptive Onboarding',
    tagline: 'Frictionless entry for every customer and partner.',
    icon: Users,
    accent: '#ff5d22',
    overview: 'AppGallop\'s Adaptive Onboarding engine handles the full spectrum of B2B and B2C entry flows — from KYC and credit checks to regional tax mapping and partner tier assignment. Reduce activation time from weeks to hours.',
    bullets: [
      'Automated KYC & identity verification across 40+ markets',
      'Dynamic B2B / B2C onboarding flows with conditional logic',
      'Partner tier assignment, contract templating & e-sign',
      'Regional tax & compliance mapping at point of activation',
      'Self-serve portal with real-time status tracking',
    ],
    stats: [{ v:'85%', l:'Reduction in manual onboarding steps' },{ v:'< 4 hrs', l:'Average partner activation time' },{ v:'40+', l:'Markets with pre-built compliance rules' }],
    faqs: [
      { q:'Can onboarding flows be customised per region?', a:'Yes. Rules are configured per market with no code changes required, covering local KYC requirements, tax IDs, and compliance obligations.' },
      { q:'Does it integrate with existing CRM data?', a:'AppGallop ships pre-built connectors for Salesforce, HubSpot, and SAP CX. Custom REST or webhook integration is also supported.' },
    ],
  },
  {
    slug: 'ai-powered-quote-to-cash',
    title: 'AI-Powered Quote-to-Cash',
    tagline: 'Every quote valid, every deal profitable.',
    icon: Layers,
    accent: '#ff5d22',
    overview: 'Revsimply CPQ turns complex product configurations into commercially valid, technically feasible quotes in minutes. AI validates margin rules, checks discount guardrails, and routes approvals automatically — so sales teams close faster without finance surprises.',
    bullets: [
      'AI-assisted product configuration with real-time feasibility checks',
      'Centralised discount governance and margin guardrails',
      'Multi-currency, multi-tax quote generation with e-sign',
      'Automated approval routing based on deal size and deviation',
      'Quote-to-order handoff with zero re-keying',
    ],
    stats: [{ v:'10x', l:'Faster quote generation vs manual CPQ' },{ v:'0%', l:'Revenue leakage with margin guardrails' },{ v:'60%', l:'Reduction in approval cycle time' }],
    faqs: [
      { q:'How does AI validate quote feasibility?', a:'The engine cross-checks product dependencies, service availability in the requested region, and pricing rules in real time before the rep ever hits submit.' },
      { q:'Can we enforce different discount tiers per partner level?', a:'Fully configurable. Discount matrices are set per partner tier, product category, and deal size with automated escalation for exceptions.' },
    ],
  },
  {
    slug: 'digital-order-orchestration',
    title: 'Digital Order Orchestration',
    tagline: 'From accepted order to live service — automatically.',
    icon: Activity,
    accent: '#ff5d22',
    overview: 'AppGallop\'s Order Management layer automates the full customer lifecycle from Order to Revenue. MACD operations, multi-vendor provisioning, and fallback handling are all driven by a single event-based orchestration engine — with real-time visibility at every step.',
    bullets: [
      'Event-driven order decomposition and multi-vendor provisioning',
      'Full MACD (Move, Add, Change, Disconnect) lifecycle automation',
      'Order fallback, retry, and exception handling workflows',
      'Real-time status visibility for ops and end-customers',
      'Native integration to CRM, ERP, and NMS/OSS layers',
    ],
    stats: [{ v:'100%', l:'Automated order-to-activation with zero touch' },{ v:'< 60s', l:'Average API provisioning response time' },{ v:'99.9%', l:'Order completion rate across all environments' }],
    faqs: [
      { q:'What happens when a downstream provisioning API fails?', a:'AppGallop automatically triggers configured fallback logic, notifies the relevant ops queue, and retries on the defined schedule — all logged for audit.' },
      { q:'How is order status surfaced to customers?', a:'Through the self-serve portal and optional webhook notifications. Customers see a live status timeline from order acceptance to service activation.' },
    ],
  },
  {
    slug: 'subscription-billing-automation',
    title: 'Subscription & Billing Automation',
    tagline: 'Bill everything. Miss nothing.',
    icon: Zap,
    accent: '#ff5d22',
    overview: 'From subscription billing to usage-based CDR rating and telecom mediation, AppGallop handles the full monetisation stack. Invoices are generated, tax-calculated, and dispatched automatically — with a full audit trail and GL-ready output on every cycle.',
    bullets: [
      'Subscription, usage-based, and hybrid billing in one engine',
      'High-volume CDR mediation and rating (millions per day)',
      'Multi-currency invoicing with automated tax calculation',
      'Revenue recognition rules applied per contract and jurisdiction',
      'GL-ready output directly to ERP on every billing run',
    ],
    stats: [{ v:'99.999%', l:'Billing accuracy rate across all runs' },{ v:'150M+', l:'CDR records rated per month' },{ v:'40%', l:'Reduction in billing disputes post-automation' }],
    faqs: [
      { q:'How does usage-based billing handle real-time data?', a:'AppGallop ingests usage events via streaming APIs, applies rating rules in near-real-time, and accumulates charges against the active subscription.' },
      { q:'Can we run billing in multiple currencies simultaneously?', a:'Yes. Each customer record carries currency and tax jurisdiction settings. Invoicing runs per-currency with FX conversion applied at configured rates.' },
    ],
  },
  {
    slug: 'revenue-financial-compliance',
    title: 'Revenue & Financial Compliance',
    tagline: 'Every transaction audit-ready from day one.',
    icon: ShieldCheck,
    accent: '#ff5d22',
    overview: 'AppGallop enforces revenue recognition standards, tax compliance, and GL mapping at the transaction level — not as a downstream reconciliation step. Whether you\'re operating under ASC 606, IFRS 15, or region-specific tax regimes, the compliance layer is built in.',
    bullets: [
      'ASC 606 / IFRS 15 compliant revenue recognition engine',
      'Automated TDS, GST, VAT, and sales tax calculation & filing',
      'GL account mapping per product, partner, and jurisdiction',
      'Deferred revenue tracking with automated release schedules',
      'SOC 2 Type II and ISO 27001 certified infrastructure',
    ],
    stats: [{ v:'100%', l:'Transaction-level GL mapping coverage' },{ v:'30+', l:'Tax regimes supported out of the box' },{ v:'SOC 2', l:'Type II & ISO 27001 certified' }],
    faqs: [
      { q:'Does AppGallop handle deferred revenue automatically?', a:'Yes. When a subscription is paid upfront, the deferred revenue balance is tracked and released to recognised revenue on the configured schedule.' },
      { q:'How does the platform stay current with tax regulation changes?', a:'AppGallop maintains a managed tax content library updated quarterly, with the option for customers to override rules for edge-case jurisdictions.' },
    ],
  },
  {
    slug: 'partner-channel-management',
    title: 'Partner & Channel Management',
    tagline: 'Your entire partner ecosystem in one place.',
    icon: Globe,
    accent: '#ff5d22',
    overview: 'AppGallop\'s PRM layer manages the full partner lifecycle — from onboarding and tier management through deal registration, co-sell workflows, commissioning, and performance reporting. Partners get a white-labelled self-serve portal; you get full visibility.',
    bullets: [
      'Full partner lifecycle: onboarding, tiering, contracts, and offboarding',
      'Deal registration, co-sell support, and opportunity management',
      'Configurable commission and incentive calculation engine',
      'White-label partner portal with custom domain support',
      'Real-time partner performance dashboards and reports',
    ],
    stats: [{ v:'50K+', l:'Partners managed through the platform' },{ v:'50%', l:'Reduction in partner activation time' },{ v:'100%', l:'Commission accuracy with automated engine' }],
    faqs: [
      { q:'Can partners have their own branded portal?', a:'Yes. Each partner tier can be assigned a white-label portal with custom domain, branding, and product catalog visibility settings.' },
      { q:'How does the commission engine handle multi-tier structures?', a:'Commission rules are configured per tier, product category, and deal type. Calculations run automatically at invoice close and are auditable at line-item level.' },
    ],
  },
];

/* ─── Sticky nav ────────────────────────────────── */
const StickyNav = ({ active }:{ active:string }) => (
  <div className="sticky top-[72px] z-40 bg-white/90 backdrop-blur-xl border-b border-black/5 shadow-sm">
    <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto py-2 scrollbar-none">
      {CAPS.map(c => (
        <a key={c.slug} href={`#${c.slug}`}
          className={`flex-shrink-0 px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest transition-all duration-200 ${
            active === c.slug
              ? 'bg-[#ff5d22] text-white shadow-md shadow-[#ff5d22]/20'
              : 'text-black/50 hover:text-black hover:bg-slate-100'
          }`}>
          {c.title}
        </a>
      ))}
    </div>
  </div>
);

/* ─── Capability section ────────────────────────── */
const CapSection = ({ cap, idx }:{ cap:typeof CAPS[0]; idx:number }) => {
  const isEven = idx % 2 === 0;
  const Icon = cap.icon;

  return (
    <section id={cap.slug} className={`relative overflow-hidden scroll-mt-[120px] ${isEven ? 'bg-white' : 'bg-slate-950'}`}>
      <style>{`@keyframes capOrbFloat{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(38px,-28px) scale(1.07)}66%{transform:translate(-22px,18px) scale(.94)}}`}</style>

      {/* subtle dot pattern */}
      <div className={`absolute inset-0 ${isEven
        ? 'bg-[radial-gradient(rgba(0,0,0,0.025)_1px,transparent_1px)] bg-[length:28px_28px]'
        : 'bg-[radial-gradient(rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:28px_28px]'}`} />
      {!isEven && <ParticleCanvas color="#ff5d22" count={45} />}
      <Orb style={{ top:'-5%', right:'-6%' }} color="#ff5d22" size={isEven?260:350} dur={isEven?18:22} />
      {!isEven && <Orb style={{ bottom:'-8%', left:'-4%' }} color="#3b82f6" size={280} dur={24} delay={3} />}

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center">

        {/* ── Left: text ── */}
        <div className={isEven ? '' : 'order-last lg:order-first'}>
          <Reveal>
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest mb-5 border ${
              isEven
                ? 'bg-[#ff5d22]/8 border-[#ff5d22]/20 text-[#ff5d22]'
                : 'bg-white/10 border-white/20 text-white/70'
            }`}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#ff5d22]" />
              Core Capability {String(idx+1).padStart(2,'0')}
            </span>
          </Reveal>

          <Reveal delay={0.08}>
            <h2 className={`text-3xl md:text-4xl lg:text-[2.6rem] font-black tracking-tighter leading-[1.08] mb-3 ${isEven ? 'text-slate-950' : 'text-white'}`}>
              {cap.title.split(' ').slice(0,-1).join(' ')}{' '}
              <span className="bg-gradient-to-r from-[#ff5d22] to-red-500 bg-clip-text text-transparent">
                {cap.title.split(' ').slice(-1)[0]}
              </span>
            </h2>
          </Reveal>

          <Reveal delay={0.12}>
            <p className={`text-base font-semibold mb-5 ${isEven ? 'text-slate-500' : 'text-white/60'}`}>{cap.tagline}</p>
          </Reveal>

          <Reveal delay={0.16}>
            <p className={`text-sm leading-relaxed mb-7 ${isEven ? 'text-slate-600' : 'text-white/70'}`}>{cap.overview}</p>
          </Reveal>

          <Reveal delay={0.2}>
            <ul className="space-y-2.5 mb-8">
              {cap.bullets.map((b,i)=>(
                <li key={i} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-md bg-[#ff5d22]/12 border border-[#ff5d22]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle size={11} className="text-[#ff5d22]" />
                  </div>
                  <span className={`text-[13px] font-medium leading-snug ${isEven ? 'text-slate-700' : 'text-white/75'}`}>{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.24}>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact"
                className="px-6 py-2.5 rounded-lg bg-[#ff5d22] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#d41820] transition-all shadow-lg hover:shadow-xl hover:shadow-[#ff5d22]/20 flex items-center gap-2">
                Book a Demo <ArrowRight size={13} />
              </Link>              
            </div>
          </Reveal>
        </div>

        {/* ── Right: stat cards ── */}
        <div className="space-y-4">
          {/* stat row */}
          <div className="grid grid-cols-3 gap-3">
            {cap.stats.map((s,i)=>(
              <Reveal key={i} delay={0.1+i*.08}>
                <TCard className={`rounded-2xl p-4 border text-center relative overflow-hidden ${
                  isEven
                    ? 'bg-white border-black/8 shadow-sm hover:shadow-md'
                    : 'bg-white/5 border-white/10 hover:bg-white/8'
                }`}>
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/50 to-transparent" />
                  <div className="text-2xl font-black text-[#ff5d22] leading-none">{s.v}</div>
                  <div className={`text-[9px] font-bold uppercase tracking-wide mt-1.5 leading-tight ${isEven?'text-slate-500':'text-white/50'}`}>{s.l}</div>
                </TCard>
              </Reveal>
            ))}
          </div>

          {/* feature highlight box */}
          <Reveal delay={0.28}>
            <div className={`rounded-2xl p-6 border relative overflow-hidden ${
              isEven
                ? 'bg-gradient-to-br from-slate-50 to-white border-black/8 shadow-sm'
                : 'bg-gradient-to-br from-slate-900 to-slate-950 border-[#ff5d22]/20'
            }`}>
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/60 to-transparent" />
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#ff5d22]/12 border border-[#ff5d22]/25 flex items-center justify-center">
                  <Icon size={20} className="text-[#ff5d22]" />
                </div>
                <div>
                  <div className={`text-[9px] font-black uppercase tracking-widest mb-0.5 ${isEven?'text-black/40':'text-white/40'}`}>How it works</div>
                  <div className={`text-sm font-black ${isEven?'text-black':'text-white'}`}>{cap.title}</div>
                </div>
              </div>
              <div className="space-y-2">
                {cap.bullets.slice(0,3).map((b,i)=>(
                  <div key={i} className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded flex items-center justify-center text-[9px] font-black flex-shrink-0 ${
                      isEven ? 'bg-[#ff5d22]/10 text-[#ff5d22]' : 'bg-[#ff5d22]/20 text-[#ff5d22]'
                    }`}>{i+1}</span>
                    <span className={`text-xs font-medium ${isEven?'text-slate-600':'text-white/65'}`}>{b.split(' ').slice(0,7).join(' ')}…</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* FAQ */}
          <div>
            {cap.faqs.map((faq,i)=><FAQ key={i} q={faq.q} a={faq.a} i={i} />)}
          </div>
        </div>
      </div>

      {/* section divider line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/20 to-transparent" />
    </section>
  );
};

/* ─── Page ──────────────────────────────────────── */
export default function CoreCapabilitiesPage() {
  const [activeSlug, setActiveSlug] = useState(CAPS[0].slug);

  // track which section is in view for sticky nav highlight
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    CAPS.forEach(c => {
      const el = document.getElementById(c.slug);
      if (!el) return;
      const obs = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) setActiveSlug(c.slug);
      }, { rootMargin: '-40% 0px -55% 0px' });
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  return (
    <div className="bg-white overflow-x-hidden">
      <style>{`@keyframes capOrbFloat{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(38px,-28px) scale(1.07)}66%{transform:translate(-22px,18px) scale(.94)}}`}</style>

      {/* ── Hero ── */}
      <section className="relative min-h-[60vh] flex flex-col justify-center bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden px-6 pb-10" style={{ paddingTop: 100 }}>
        <ParticleCanvas color="#ff5d22" count={70} />
        <Orb style={{ top:'-5%', right:'-6%' }} color="#ff5d22" size={480} dur={18} />
        <Orb style={{ bottom:'-10%', left:'-4%' }} color="#3b82f6" size={380} dur={22} delay={3} />

        <div className="relative z-20 max-w-5xl mx-auto w-full">
          <Reveal>
            <Link to="/" className="inline-flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-black/40 hover:text-[#ff5d22] transition-colors mb-6">
              <ArrowLeft size={11} /> Home
            </Link>
          </Reveal>
          <Reveal delay={0.06}>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/6 text-slate-700 text-[9px] font-black uppercase tracking-widest mb-5 border border-slate-200">
              <span className="w-2 h-2 rounded-full bg-[#ff5d22]" />
              Platform · Core Capabilities
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.07] text-slate-950 mb-5">
              Orchestrate the Entire{' '}
              <span className="bg-gradient-to-r from-[#ff5d22] to-red-500 bg-clip-text text-transparent">
                Revenue Lifecycle
              </span>
            </h1>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-base text-slate-600 font-medium leading-relaxed max-w-2xl mb-8">
              Six foundational capabilities that automate every layer of the digital revenue lifecycle — from customer activation to the general ledger.
            </p>
          </Reveal>

          {/* mini card grid — anchors to each section */}
          <Reveal delay={0.18}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {CAPS.map((c,i) => (
                <a key={c.slug} href={`#${c.slug}`}
                  className="group flex items-center gap-3 p-4 rounded-2xl border border-black/8 bg-white hover:border-[#ff5d22]/30 hover:shadow-lg hover:shadow-[#ff5d22]/8 transition-all duration-300">
                  <div className="w-9 h-9 rounded-xl bg-[#ff5d22]/8 border border-[#ff5d22]/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <c.icon size={17} className="text-[#ff5d22]" />
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="text-[9px] font-black uppercase tracking-widest text-black/30 mb-0.5">0{i+1}</div>
                    <div className="text-xs font-black text-black group-hover:text-[#ff5d22] transition-colors leading-tight">{c.title}</div>
                  </div>
                  <ChevronDown size={13} className="text-black/20 group-hover:text-[#ff5d22] -rotate-90 transition-colors flex-shrink-0" />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Sticky nav ── */}
      <StickyNav active={activeSlug} />

      {/* ── Capability sections ── */}
      {CAPS.map((c,i) => <CapSection key={c.slug} cap={c} idx={i} />)}

      {/* ── CTA ── */}
      <section className="relative bg-slate-950 py-20 px-6 overflow-hidden">
        <ParticleCanvas color="#ff5d22" count={55} />
        <Orb style={{ top:'10%', left:'8%' }} color="#ff5d22" size={420} dur={20} />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/70 text-[9px] font-black uppercase tracking-widest mb-5">
              Ready to see it live?
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-white mb-4">
              All six capabilities.{' '}
              <span className="bg-gradient-to-r from-[#ff5d22] to-red-400 bg-clip-text text-transparent">
                One unified platform.
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-white/65 text-base leading-relaxed mb-8 max-w-xl mx-auto">
              See how AppGallop's capability stack connects to automate your entire revenue lifecycle — from first customer touch to ledger close.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="px-8 py-3.5 rounded-lg bg-[#ff5d22] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#d41820] transition-all shadow-xl hover:shadow-[#ff5d22]/25 flex items-center gap-2">
                Book a Demo <ArrowRight size={14} />
              </Link>
              <Link to="/platform/integrations" className="px-8 py-3.5 rounded-lg border border-white/20 text-white/70 text-[10px] font-black uppercase tracking-widest hover:border-white/40 hover:text-white transition-all">
                View Integrations
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <ContactSection id="contact" />
    </div>
  );
}
